export interface CoachingInfluenceInput {
  rawMarginEdge: number
  interventionCount: number
  alignedResearchCount: number
  tacticalCohesion: number
  seed: string
}

export interface CoachingInfluence {
  rawMarginEdge: number
  expectedMarginEdge: number
  executionRisk: number
  executionVariance: number
  realisedMarginEdge: number
  interventionLevel: 'staff' | 'focused' | 'heavy'
}

const hashUnit = (seed: string, salt: string) => {
  let hash = 2166136261
  for (const character of `${salt}|${seed}`) {
    hash ^= character.charCodeAt(0)
    hash = Math.imul(hash, 16777619)
  }
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x7feb352d)
  hash ^= hash >>> 15
  hash = Math.imul(hash, 0x846ca68b)
  hash ^= hash >>> 16
  return (hash >>> 0) / 4294967296
}

export const seededNormal = (seed: string, salt: string) => {
  const first = Math.max(Number.EPSILON, hashUnit(seed, `${salt}:a`))
  const second = hashUnit(seed, `${salt}:b`)
  return Math.sqrt(-2 * Math.log(first)) * Math.cos(2 * Math.PI * second)
}

export const diminishCoachingEdge = (rawMarginEdge: number, cap = 12) =>
  cap * Math.tanh(rawMarginEdge / cap)

export const calculateCoachingInfluence = ({
  rawMarginEdge,
  interventionCount,
  alignedResearchCount,
  tacticalCohesion,
  seed,
}: CoachingInfluenceInput): CoachingInfluence => {
  if (interventionCount === 0 && alignedResearchCount === 0) {
    return {
      rawMarginEdge,
      expectedMarginEdge: 0,
      executionRisk: 0,
      executionVariance: 0,
      realisedMarginEdge: 0,
      interventionLevel: 'staff',
    }
  }

  const expectedMarginEdge = diminishCoachingEdge(rawMarginEdge)
  const excessChanges = Math.max(0, interventionCount - 2)
  const researchProtection = Math.min(excessChanges, alignedResearchCount) * 0.8
  const cohesionProtection = Math.max(0, tacticalCohesion - 50) / 25
  const executionRisk = Math.max(0, Math.min(8, excessChanges * 2 - researchProtection - cohesionProtection))
  const executionVariance = seededNormal(seed, 'coach-execution') * executionRisk
  const realisedMarginEdge = expectedMarginEdge + executionVariance

  return {
    rawMarginEdge,
    expectedMarginEdge,
    executionRisk,
    executionVariance,
    realisedMarginEdge,
    interventionLevel: interventionCount >= 4 ? 'heavy' : 'focused',
  }
}

export const expectedMatchMargin = (
  ourRating: number,
  opponentRating: number,
  venue: 'home' | 'away',
) => (ourRating - opponentRating) * 1.5 + (venue === 'home' ? 5 : -5)

export const resolveFixtureMargin = (expectedMargin: number, seed: string, standardDeviation = 20) =>
  expectedMargin + seededNormal(seed, 'match-day-performance') * standardDeviation

export const normalWinProbability = (expectedMargin: number, standardDeviation = 20) => {
  const z = expectedMargin / standardDeviation
  const sign = z < 0 ? -1 : 1
  const absolute = Math.abs(z) / Math.sqrt(2)
  const t = 1 / (1 + 0.3275911 * absolute)
  const approximation = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-absolute * absolute)
  const cdf = 0.5 * (1 + sign * approximation)
  return Math.max(0, Math.min(1, cdf))
}
